/**
 * "Handed in" is the student's own act, and only a non-DRAFT submission records
 * it. A teacher opening "Upload responses" for a student creates a DRAFT shell
 * before any work exists, so status alone — not the mere existence of a row —
 * says whether that student still owes a response.
 */
export const isHandedIn = (submission) =>
  String(submission?.status || '').toUpperCase() !== 'DRAFT';

/**
 * A class response a teacher put in (bulk upload or an upload on the student's
 * behalf) and then reassigned (JED-1473). This is only the shape of the row;
 * on its own it decides nothing. Whether this reader may upload it again is
 * canUploadAgain below, which adds the server's per-reader permission, and that
 * is what the upload menu and the upload page use. `teacherUploaded` comes from
 * the server; older servers omit it. My Drafts documents are never re-uploaded
 * this way.
 */
export const isReassignedTeacherUpload = (submission) =>
  String(submission?.status || '').toUpperCase() === 'RESUBMISSION_REQUESTED' &&
  submission?.teacherUploaded === true &&
  submission?.type !== 'DOCUMENT';

/**
 * Whether this reader may upload a reassigned teacher upload again, as the
 * server decides it (JED-1473): only the marker holding the response, while they
 * still teach the class, and only until the student starts revising it. The
 * server says so per reader in `canUploadAgain`; without it (an older server, a
 * co-teacher, a revision the student has started) the upload is not offered, so
 * the teacher is never handed an editable page the server would refuse.
 */
export const canUploadAgain = (submission) =>
  isReassignedTeacherUpload(submission) && submission?.canUploadAgain === true;

/**
 * Context-aware visibility for student roster.
 *
 * During active feedback run (bulk upload in progress), show ALL submissions
 * including DRAFT status so teachers can see all uploaded students immediately.
 * Otherwise, only show handed-in submissions.
 */
export const isVisibleInRoster = (submission, { isRunActive = false } = {}) => {
  if (isRunActive) {
    return true;
  }
  return isHandedIn(submission);
};

export default isHandedIn;
